import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useIsWebView, useWebViewLoading } from '../../../shared/hooks';
import { PdfButton } from '../../../shared/components/PdfButton';
import { formatDateTimeMinute } from '../../../shared/utils/datetime';

import type { Inspection } from '../types';
import { INSPECTION_STATUS_LABELS } from '../types';
import { useInspectionDetail } from '../hooks/useInspectionDetail';

import './InspectionDetail.css';

// --- Helper Components ---

const InfoRow: React.FC<{ label: string, value: string | undefined }> = ({ label, value }) => (
  <div className="info-row">
    <p className="info-label">{label}</p>
    <p className="info-value">{value || 'ー'}</p>
  </div>
);

const toCircledNumber = (num: number): string => {
  const circledNumbers = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];
  return circledNumbers[num - 1] || `(${num})`;
};

const InspectionItemRows: React.FC<{ items: Inspection['inspectionItems'] }> = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <>
        <InfoRow label="点検区分" value={undefined} />
        <InfoRow label="点検場所" value={undefined} />
      </>
    );
  }

  if (items.length === 1) {
    return (
      <>
        <InfoRow label="点検区分" value={items[0].category} />
        <InfoRow label="点検場所" value={items[0].location} />
      </>
    );
  }

  return (
    <>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <InfoRow label={`点検区分${toCircledNumber(index + 1)}`} value={item.category} />
          <InfoRow label={`点検場所${toCircledNumber(index + 1)}`} value={item.location} />
        </React.Fragment>
      ))}
    </>
  );
};

// --- Main Component ---

function InspectionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isWebView = useIsWebView();

  const inspectionId = id ? parseInt(id, 10) : 0;
  const { data: inspection, isLoading, error } = useInspectionDetail(inspectionId);

  const storageKey = `viewedInspectionDocs_${id}`;

  const [viewedDocs, setViewedDocs] = useState<Record<string, boolean>>(() => {
    const savedState = sessionStorage.getItem(storageKey);
    return savedState ? JSON.parse(savedState) : {};
  });

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(viewedDocs));
  }, [viewedDocs, storageKey]);

  // Flutter WebViewのローディング制御（データ取得完了時 & ページ再表示時）
  // コールバックrefを使用してDOMが実際に描画された後にhideLoadingを呼ぶ
  const contentRef = useWebViewLoading(isLoading);

  // ローディング中はFlutter側のオーバーレイに任せるため、空のコンテナを返す
  if (isLoading) {
    return <div className="detail-container" />;
  }

  if (error || !inspection) {
    return <div>点検情報が見つかりません。</div>;
  }

  const handleViewDoc = (docName: string) => {
    setViewedDocs(prev => ({ ...prev, [docName]: true }));
  };

  const navigateToSign = () => {
    navigate(`/inspections/${id}/sign`, { state: { fromStatus: inspection.status } });
  };

  const getActionButtons = () => {
    switch (inspection.status) {
      case 'result_confirmation':
        const resultConfirmationDocs = ['点検結果報告書'];
        const allDocsViewed = resultConfirmationDocs.every(doc => viewedDocs[doc]);
        return [(
          <button key="1" className="button-primary" disabled={!allDocsViewed} onClick={navigateToSign}>
            サインへ進む
          </button>
        )];

      default:
        return [];
    }
  };

  const getPageTitle = () => {
    switch (inspection.status) {
      case 'received':
        return '依頼内容';
      case 'visit_confirmed':
      case 'in_progress':
        return '点検情報';
      case 'result_confirmation':
        return '点検結果確認';
      case 'completed':
        return '点検完了';
      default:
        return '点検詳細';
    }
  };

  const getBasicInfo = () => (
    <>
      <div className="status-row">
        <span className="status-label">受付状況：</span>
        <span className="status-value">{INSPECTION_STATUS_LABELS[inspection.status]}</span>
      </div>
      <InfoRow label="受付日" value={formatDateTimeMinute(inspection.requestDate)} />
      <InspectionItemRows items={inspection.inspectionItems} />
      <InfoRow label="点検事業者" value={inspection.contractor} />
      <InfoRow label="受付担当者" value={inspection.receptionManager} />
      {inspection.visitDate !== '日程調整中' && (
        <>
          <InfoRow label="訪問日時" value={formatDateTimeMinute(inspection.visitDate)} />
          <InfoRow label="点検担当者" value={inspection.manager} />
        </>
      )}
    </>
  );

  const mainContent = (() => {
    switch (inspection.status) {
      case 'received':
        return getBasicInfo();

      case 'visit_confirmed':
      case 'in_progress':
        return getBasicInfo();

      case 'result_confirmation':
        const resultConfirmationDocs = ['点検結果報告書'];
        return (
          <>
            <p className="instruction-text">すべての書類をご確認いただき、サイン画面へお進みください。</p>
            {resultConfirmationDocs.map(doc =>
              <PdfButton
                key={doc}
                name={doc}
                viewed={!!viewedDocs[doc]}
                onView={() => handleViewDoc(doc)}
                url={inspection.resultReportUrl}
              />
            )}
          </>
        );

      case 'completed':
        return (
          <>
            {getBasicInfo()}
            <InfoRow label="点検結果" value={inspection.inspectionResult} />
            {inspection.resultReportUrl && (
              <PdfButton
                name="点検結果報告書"
                viewed={true}
                onView={() => handleViewDoc('点検結果報告書')}
                url={inspection.resultReportUrl}
              />
            )}
          </>
        );

      default:
        return <p>情報を取得できませんでした。</p>;
    }
  })();

  const buttons = getActionButtons();

  return (
    <div ref={contentRef} className="detail-container">
      <main className="detail-content">
        <h2 className="content-title">{getPageTitle()}</h2>
        {mainContent}
      </main>
      <footer className="detail-footer">
        {buttons}
        {!isWebView && (
          <button className="button-secondary" onClick={() => navigate(-1)}>戻る</button>
        )}
      </footer>
    </div>
  );
}

export default InspectionDetail;
